/**
 * 파일명 : usr0101m.jsx
 * 설명 : 휴가현황화면
 * 수정내역
 ************************************************
 * Version    수정일자      수정자    수정내용      
 ************************************************
 * 1.0       2020-08-10   윤성수     최초작성
 * 1.1       2024-03-21   방진선     직원명 콤보로 수정 및 엔터키 조회
 */

/************************************************
 * Import 영역  
 ************************************************/
/*  React  */
import React from 'react';
import locale from 'antd/lib/locale/ko_KR';
import koKR from 'antd/es/calendar/locale/ko_KR';

/* Reducer */
import { useAppSelector, useAppDispatch } from '../../../redux/hooks';
import { Usr0101mAction } from '../../../redux/slice/usr/usr0101m_slice';
import { IUserOffHisState, IUserItemState, IOffrDcdItemState, IDeptCdItemState, IInquiryCndsState, ICalendarState } from '../../../redux/models/usr/usr0101m_model';

/*  AntD  */
import { Table, Button, Space, Select, Row, Col, Layout, Typography, Collapse, DatePicker, Descriptions, ConfigProvider, Radio, Calendar, Badge, Tooltip } from 'antd';
import { SearchOutlined, CaretRightOutlined } from '@ant-design/icons';
import type { BadgeProps } from 'antd';

/*  업무  */
import * as commonProxy from 'libs/CommonProxy';
import * as common from 'libs/common';
import * as commonDate from 'libs/commonDate';

import dayjs, { Dayjs } from 'dayjs';

/************************************************
 * 전역변수 영역
 ************************************************/

const Usr0101m = (props:any) => {
    /************************************************
     * 화면 초기화 영역  
     ************************************************/
    /**
     * 기능 : State / Reducer 선언
     * param : 
     */
    const dispatch = useAppDispatch();
    const inquiryCnds      = useAppSelector((state) => state.Usr0101m.inquiryCnds);
    const userList         = useAppSelector((state) => state.Usr0101m.userList);
    const deptCdList       = useAppSelector((state) => state.Usr0101m.deptCdList);
    const offrDcdList      = useAppSelector((state) => state.Usr0101m.offrDcdList);
    const calendarInfoList = useAppSelector((state) => state.Usr0101m.calendarInfoList) || [];
    const userOffHis       = useAppSelector((state) => state.Usr0101m.userOffHis);
    const userOffStatus    = useAppSelector((state) => state.Usr0101m.userOffStatus);
    const pageSize         = useAppSelector((state) => state.Usr0101m.pageSize);
    const current          = useAppSelector((state) => state.Usr0101m.current);
    const [mode, setMode] = React.useState('1');
    const [calValue, setCalValue] = React.useState(dayjs());

    let tmp = 0;
    const [tableKey, setTableKey] = React.useState<number>(0);
    const [contentHeight, setContentHeight] = React.useState<number>(window.innerHeight);
    
    
    /**
     * 기능 : componentDidMount
     * 설명 : 화면 최초 로드시에 필요한 기능을 구현한다.
     * param : 
     * return : None
     */
    React.useEffect(() => {
        const headerHeight = 335;  // Header 높이
        setContentHeight(window.innerHeight - headerHeight);
        
        const handleResize = () => {
            setContentHeight(window.innerHeight - headerHeight);
            let tmpKey = ++tmp;
            tmp = tmpKey;
            setTableKey(tmpKey);
        };

        if (!common.isAdmin()) {
            dispatch(Usr0101mAction.setInquiryCnds({ ...inquiryCnds, empNo : common.getEmpNo(), deptCd : common.getDeptCd() }));
        }
        setOffrDcdList();
        fn_DeptCds();
        fn_User();
        fn_Search();

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize); // cleanup
        };
    }, []);


    /* Render 변수 선언영역 */
    /* AntD */
    const { Content }     = Layout;
    const { Text }        = Typography;
    const { Panel }       = Collapse;
    const { RangePicker } = DatePicker;

    /* Table Columns */
    const columns = React.useCallback(() => {
        const arr:any = [
            {
                title: () => <div style={{ textAlign: 'center' }}>휴가구분</div>,
                dataIndex: 'offDnm',
                key: 'offDnm',
                align: 'center',
                width: '10%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>사용자명</div>,
                dataIndex: 'userNm',
                key: 'userNm',
                align: 'center',
                width: '10%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>직위</div>,
                dataIndex: 'postNm',
                key: 'postNm',
                align: 'center',
                width: '10%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>부서</div>,
                dataIndex: 'deptNm',
                key: 'deptNm',
                align: 'center',
                width: '10%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>행선지</div>,
                dataIndex: 'dest',
                key: 'dest',
                align: 'left',
                width: '20%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>사유</div>,
                dataIndex: 'offRsn',
                key: 'offRsn',
                align: 'left',
                width: '20%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>시작일자</div>,
                dataIndex: 'offStDt',
                key: 'offStDt',
                align: 'center',
                render: (text:string) => commonDate.maskDate(text, 'YYYY-MM-DD'),
                width: '10%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>종료일자</div>,
                dataIndex: 'offEdDt',
                key: 'offEdDt',
                align: 'center',
                render: (text:string) => commonDate.maskDate(text, 'YYYY-MM-DD'),
                width: '10%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>상세보기</div>,
                key: 'action',
                render: (_text:string, record:IUserOffHisState) => (
                    <Button
                        id='btn_showDetail'
                        name='btn_showDetail'
                        className='background-color-default-button'
                        size='small'
                        onClick={(e) => onClick(e, record)}
                    >
                        상세보기
                    </Button>
                ),
                align: 'center',
                width: '9%',
            }];
            return arr;
    }, []);

    /**
     * 기능 : 초기화 이벤트 선언
     * param : 
     */
    const fn_Reset = () => {
        const initInquiryCnds:IInquiryCndsState = {
            empNo : !common.isAdmin()  && !common.isDeptHdr() ? common.getEmpNo() : '',
            deptCd: !common.isAdmin() && common.isEmptyString(inquiryCnds.deptCd) ? common.getDeptCd() : inquiryCnds.deptCd,

        }
        dispatch(Usr0101mAction.initState(initInquiryCnds));
        dispatch(Usr0101mAction.setCurrent(1));
    };

    /**
     * 기능 : 결과초기화 이벤트 선언
     * param : 
     */
    const fn_ResetResult = () => {
        dispatch(Usr0101mAction.setUserOffHis([]));
    };


    /**
     *  기능 : RangePicker 데이터 변경
     *  설명 : RangePicker 데이터 변경값을 받는다
     *  param :
     *  return : None
     */
    const calendar_onChange = (_:any, dateStrings:any) => {
        if(Array.isArray(dateStrings)){
            const startDate = dateStrings[0].split('-').join('');
            const endDate   = dateStrings[1].split('-').join('');
            dispatch(Usr0101mAction.setInquiryCnds({ ...inquiryCnds, offStDt : startDate, offEdDt : endDate }));
        } else {

            if (mode === '1') {
                dispatch(Usr0101mAction.setInquiryCnds({ ...inquiryCnds, baseYy : dateStrings }));
            } else {
                fn_ResetResult();
                dispatch(Usr0101mAction.setInquiryCnds({ ...inquiryCnds, baseYy : dateStrings.substring(0, 4), baseYm : dateStrings.split('-').join('') }));
                setCalValue(dayjs().year(parseInt(dateStrings.substring(0, 4))).month(parseInt(dateStrings.split('-').join('').substring(4, 6)) - 1));
            }
        }
    };

    /**
     * 기능 : onClick 이벤트 선언
     * param : 
     */
    const onClick = async (e:any, ...record:IUserOffHisState[]) => {
        const buttonName = e.currentTarget.name;
        switch (buttonName) {
            case 'btn_Search': {
                fn_Search();
                break;
            }

            case 'btn_Reset': {
                fn_Reset();
                break;
            }

            case 'btn_Dept': {
                const accept = (deptCd:string) => {
                    const filterd = deptCdList.filter((item:IDeptCdItemState) => {
                        return item.value === deptCd;
                    });
                    if (filterd.length < 1) {
                        const param = {
                            title : '위드정보 확인',
                            contents: '본인소속 부서만 선택 가능합니다.',
                        };
        
                        common.openInfo(param);
                        return;
                    } else {
                        dispatch(Usr0101mAction.setInquiryCnds({ ...inquiryCnds, deptCd }));
                    }
                }
                const params:any = {};
                params.title = '부서 선택';
                params.popupId = 'com0101p4';
                params.accept = accept;
                params.width = 700
                common.openPopup(params);
                break;
            }

            case 'btn_showDetail': {
                const param = {
                    menuId : 'MUSR2021003',
                    props : {
                        apprNo : record[0].apprNo
                    }
                };
                common.openPage(param);
                break;
            }

            default:
        }
    };

    React.useEffect(() => {
        fn_User();
    }, [inquiryCnds.deptCd]);
    
    /**
     * 기능 : fn_User
     * 설명 : 직원명 콤보박스 데이터 조회
     * param : 
     * return : None
     **/
    const fn_User = () => {
        /* 변수선언 및 셋팅 영역 */
        // 입력파라미터
        const requestData = {
            userStCd: 'A',
            deptCd: inquiryCnds.deptCd ? inquiryCnds.deptCd : common.isAdmin() ? '' : common.getDeptCd()
        };

        /* 트렌젝션 호출 및 결과 셋팅 */
        commonProxy.callAsyncSvc(requestData, {
            txCd: 'TRUSRR0003',
            success: (responseData:any) => {
                // 사용자목록 이름순으로 정렬
                responseData.userList.sort((a:IUserItemState, b:IUserItemState) => (a.userNm > b.userNm) ? 1 : -1)
                dispatch(Usr0101mAction.setUserList(responseData.userList));
            },
        });
    };

    const userOptions = React.useCallback(() => {
        let arrUserOptions: IUserItemState[] = [];

        if (common.isAdmin() || common.isDeptHdr()) {
            arrUserOptions.push({label: '전체', value: ''});
        }

        userList.forEach((item:IUserItemState) => {
            if (!common.isAdmin() && !common.isDeptHdr()) {
                if (item.empNo === common.getEmpNo()) {
                    arrUserOptions.push({
                        label: item.userNm,
                        value: item.empNo
                    });
                }
            } else {
                arrUserOptions.push({
                    label: item.userNm,
                    value: item.empNo
                });
            }
        });

        return arrUserOptions;
    }, [userList]);


    const setOffrDcdList = () => {
        const option = {
            useYn: 'Y',
            callback: (datas:IOffrDcdItemState[]) => {
                let arrOptions:IOffrDcdItemState[] = [];

                arrOptions.push({label: '전체', value: ''});

                datas.forEach((item:IOffrDcdItemState) => {
                    arrOptions.push({
                        label: item.cdNm,
                        value: item.cd
                    });
                });

                dispatch(Usr0101mAction.setOffrDcdList(arrOptions));
            }
        };
        common.getComCodeList('008', option);
    }

    /**
     * 기능 : fn_DeptCds
     * 설명 : 부서코드 콤보박스 데이터 조회
     * param : 
     * return : None
     **/
     const fn_DeptCds = () => {
        let arrOptions: IDeptCdItemState[] = [];

        if (common.isAdmin()) {
            arrOptions.push({label: '전체', value: ''});
        }

        /* 변수선언 및 셋팅 영역 */
        // 입력파라미터
        const requestData = {
            deptStCd: 'A',
            deptCd: !common.isAdmin() ? common.getDeptCd() : '',
            inclSubDept: true,
        };

        /* 트렌젝션 호출 및 결과 셋팅 */
        commonProxy.callAsyncSvc(requestData, {
            txCd: 'TRCOMR0005',
            success: (responseData:any) => {
                // 사용자목록 이름순으로 정렬
                responseData.deptItems.sort((a:IDeptCdItemState, b:IDeptCdItemState) => (a.deptNm > b.deptNm) ? 1 : -1);

                responseData.deptItems.forEach((item:IDeptCdItemState) => {
                    if (common.isAdmin() || common.getDeptCd() === item.deptCd || common.isDeptHdr()) {
                        arrOptions.push({
                            label: item.deptNm,
                            value: item.deptCd
                        });
                    }
                });
                dispatch(Usr0101mAction.setDeptCdList(arrOptions));
            },
        });
    };

    /**
     * 기능 : 검색조회
     * param : 
     */
    const fn_Search = () => {
        fn_ResetResult();
        dispatch(Usr0101mAction.setCurrent(1));
        
        /* 변수선언 및 셋팅 영역 */
        // 입력파라미터
        const requestData = {
            ...inquiryCnds,
            empNo: !common.isAdmin()  && !common.isDeptHdr() ? common.getEmpNo()  : inquiryCnds.empNo,
            deptCd: !common.isAdmin() && common.isEmptyString(inquiryCnds.deptCd) ? common.getDeptCd() : inquiryCnds.deptCd,
            mode
        };

        /* 트렌젝션 호출 및 결과 셋팅 */
        commonProxy.callAsyncSvc(requestData, {
            txCd : 'TRUSRR0002',
            success : (responseData:any) => {
                dispatch(Usr0101mAction.setUserOffStatus(responseData.userOffStatus));
                dispatch(Usr0101mAction.setUserOffHis(responseData.userOffHis));
                dispatch(Usr0101mAction.setCalendarInfoList(responseData.calendarInfo));
            },
        });
    };

    React.useEffect(() => {
        if (mode === '2' && inquiryCnds.baseYm) {
            setCalValue(
                dayjs()
                    .year(parseInt(inquiryCnds.baseYm.substring(0, 4)))
                    .month(parseInt(inquiryCnds.baseYm.substring(4, 6)) - 1)
            );
        }

        fn_Search();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode]);

    /**
     * 기능 : 입력 후 Enter key 입력 시 조회
     * 설명 : 입력컴포넌트
     * param : 
     * return : None
     */
    const handleOnKeyUp = (e:any) => {
        if (e.key === 'Enter') {
            fn_Search(); // 조회
        }
    };

    const getListData = (value: Dayjs) => {
        let listData: { type: string; content: string }[] = [];
        const currents = calendarInfoList.filter(item => {
            return item.baseDt === value.format('YYYYMMDD');
        });

        const current = currents[0];

        if (value.month() + 1 !== parseInt(inquiryCnds.baseYm.substring(4, 6))) {
            return listData;
        }

        let filterd = userOffHis.filter((item:IUserOffHisState) => {
            return item.offStDt <= value.format('YYYYMMDD') && value.format('YYYYMMDD') <= item.offEdDt;
        });

        filterd = filterd.filter((item:IUserOffHisState) => {
            return current?.holidayYn !== 'Y' && current?.dateDcd !== '01';
        });

        if (filterd && filterd.length > 0) {
            for (let i = 0; i < filterd.length; i++) {
                listData.push({ type: 'success', content: filterd[i].userNm + ' ' + filterd[i].offDnm + ' ' + filterd[i].apprNo });
            }
        }

        return listData;
    };

    

    const dateCellRender = (
        value: Dayjs,
        listData: { type: string; content: string }[]
    ) => {
        if (!listData || listData.length < 1) {
            const currents = calendarInfoList.filter(item => {
                return item.baseDt === value.format('YYYYMMDD');
            });

            const current = currents[0];

            const dayNm = current?.holidayYn === 'Y' ? current.dateNm : '';

            return (
                <li style={{ cursor: 'pointer', padding: 8 }}>
                    <div key={value.format('YYYYMMDD')} style={{ color: 'red' }}>
                        <Badge
                            status={dayNm ? 'error' : undefined}
                            text={
                                dayNm ? (
                                    <span style={{ color: 'red' }}>
                                        {dayNm}
                                    </span>
                                ) : null
                            }
                        />
                    </div>
                </li>
            );
        }

        const renderBadgeList = (isTooltip = false) => (
            <li style={{ cursor: 'pointer', padding: 8 }}>
                <div>
                    {listData.map((item) => {
                        const contents = item.content.split(' ');
                        const apprNo = contents[2];
                        const text = `${contents[0]} ${contents[1]}`;

                        return (
                            <div
                                key={apprNo}
                                style={isTooltip ? { marginBottom: 4 } : undefined}
                            >
                                <Badge
                                    status={item.type as BadgeProps['status']}
                                    text={text}
                                    onClick={() => {
                                        const param = {
                                            menuId: 'MUSR2021003',
                                            props: {
                                                apprNo
                                            }
                                        };

                                        common.openPage(param);
                                    }}
                                />
                            </div>
                        );
                    })}
                </div>
            </li>
        );

        if (listData.length > 1) {
            return (
                <Tooltip
                    color="#ffffff"
                    placement="left"
                    title={
                        <div
                            style={{
                                minWidth: 150,
                                maxHeight: 200,
                                overflowY: 'auto'
                            }}
                        >
                            {renderBadgeList(true)}
                        </div>
                    }
                    getPopupContainer={(triggerNode) => triggerNode.parentElement!}
                >
                    {renderBadgeList()}
                </Tooltip>
            );
        }

        return renderBadgeList();
    };

    return (
        <>
            <Layout>
                <Content>
                    <Collapse
                        bordered={false}
                        defaultActiveKey={['1']}
                        expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
                        className='site-collapse-custom-collapse'
                    >
                        <Panel header='검색조건' key='1' className='site-collapse-custom-panel'>
                            <Row
                                align='middle'
                                justify='space-between'
                                style={{ marginTop: 10 }}
                            >
                                <Col flex='9.5'>
                                    <Space>
                                        <Radio.Group value={mode} onChange={(e:any) => setMode(e.target.value)}>
                                            <Radio key='mode_1' value='1'>그리드</Radio>
                                            <Radio key='mode_2' value='2'>캘린더</Radio>
                                        </Radio.Group> 
                                        <label>{mode === '1' ? '기준년도' : '기준년월'}</label>
                                        <ConfigProvider locale={locale}>
                                            {mode === '1' ? 
                                            <DatePicker
                                                style={{width: 80}}
                                                value={dayjs(inquiryCnds.baseYy)}
                                                onChange={calendar_onChange}
                                                picker='year'/>
                                            :
                                            <DatePicker
                                                allowClear={false}
                                                value={dayjs(inquiryCnds.baseYm)}
                                                onChange={calendar_onChange}
                                                picker='month'/>
                                            }
                                        </ConfigProvider>
                                        <label>휴가구분</label>
                                        <Select
                                            style={{
                                                width: 150,
                                            }}
                                            defaultValue=''
                                            optionFilterProp='label'
                                            options={offrDcdList}
                                            onKeyUp={handleOnKeyUp}
                                            onChange={val => {
                                                dispatch(Usr0101mAction.setInquiryCnds({...inquiryCnds, offDcd: val}))
                                            }}
                                            value={inquiryCnds.offDcd}
                                        />
                                        <label style={{ marginLeft : 10 }}>부서</label>
                                        <Select
                                            style={{
                                                width: 200,
                                            }}
                                            defaultValue=''
                                            optionFilterProp='label'
                                            options={deptCdList}
                                            onKeyUp={handleOnKeyUp}
                                            onChange={val => {
                                                dispatch(Usr0101mAction.setInquiryCnds({...inquiryCnds, deptCd: val}))
                                            }}
                                            value={inquiryCnds.deptCd}
                                            // ReadOnly 적용
                                            {...common.getReadonlySelectboxProps(!common.isAdmin() && !common.isDeptHdr())}
                                        />
                                        <Button
                                            name='btn_Dept'
                                            className='background-color-default-button'
                                            icon={<SearchOutlined />}
                                            onClick={onClick}
                                        >
                                        </Button>
                                        <label>직원명</label>
                                        <Select 
                                            showSearch
                                            style={{
                                                width: 120,
                                            }}
                                            defaultValue=''
                                            optionFilterProp='label'
                                            filterSort={(optionA: any, optionB: any) => {
                                                const labelA = (optionA?.label ?? '').toLowerCase();
                                                const labelB = (optionB?.label ?? '').toLowerCase();
                                                
                                                if (labelA === '전체') return -1;
                                                if (labelB === '전체') return 1;
                                                
                                                return labelA.localeCompare(labelB);
                                            }}
                                            options={userOptions()}
                                            onKeyUp={handleOnKeyUp}
                                            value={inquiryCnds.empNo}
                                            onChange={val => dispatch(Usr0101mAction.setInquiryCnds({...inquiryCnds, empNo: val}))}
                                            // ReadOnly 적용
                                            {...common.getReadonlySelectboxProps(!common.isAdmin() && !common.isDeptHdr())}
                                        ></Select>
                                        {mode === '1' ?
                                            <>
                                                <label>휴가일자</label>
                                                <ConfigProvider locale={locale}>
                                                    <RangePicker
                                                        style={{ width: 250 }}
                                                        id='tx_calendar'
                                                        allowClear={false}
                                                        onChange={calendar_onChange}
                                                        picker='date'
                                                        value={[inquiryCnds.offStDt ? dayjs(inquiryCnds.offStDt) : null, inquiryCnds.offEdDt ? dayjs(inquiryCnds.offEdDt) : null]}
                                                    />
                                                </ConfigProvider>
                                            </>
                                        : null}
                                    </Space>
                                </Col>
                                <Col flex='0.5'>
                                    <Space>
                                        <Button
                                            name='btn_Reset'
                                            className='background-color-default-button'
                                            onClick={onClick}
                                        >
                                            초기화
                                        </Button>
                                        <Button
                                            name='btn_Search'
                                            className='background-color-primary-button'
                                            icon={<SearchOutlined />}
                                            onClick={onClick}
                                        >
                                            조회
                                        </Button>
                                    </Space>
                                </Col>
                            </Row>
                        </Panel>
                    </Collapse>
                    <Row align='middle' style={{ height: 50, width:'100%' }}>
                        <Space>
                            <Col>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                    <Descriptions bordered column={3} size='small'>
                                        <Descriptions.Item label='부여휴가일수'>{userOffStatus.giveOffDays + userOffStatus.addOffDays}</Descriptions.Item>
                                        <Descriptions.Item label='사용휴가일수'>{userOffStatus.useOffDays}</Descriptions.Item>
                                        <Descriptions.Item label='잔여휴가일수'>{userOffStatus.remainOffDays}</Descriptions.Item>
                                    </Descriptions>
                                    <Space>
                                        결재 진행중 : {userOffStatus.apprReqCnt}건 (총 {userOffStatus.apprReqDays}일)
                                    </Space>
                                </div>
                            </Col>
                        </Space>
                    </Row>
                    <Row align='middle' style={{ height: 50 }}>
                        <Col>
                            <Space>
                                <img className='title-icon' alt='' />
                                <Text strong>휴가이력</Text>
                            </Space>
                        </Col>
                    </Row>
                    <Row>
                        {mode === '1' ?
                        <div key={tableKey} style={{ height: contentHeight, display: 'flex', flexDirection: 'column', width : '100%', flex: '1 1 auto', overflowY: 'auto' }}>
                            <Table
                                dataSource={userOffHis}
                                key='no'
                                columns={columns()}
                                size='small'
                                style={{width:'100%'}}
                                scroll={{y:contentHeight - 95}}
                                pagination={{
                                    pageSize: pageSize,
                                    current: current,
                                    showTotal : () => '총 ' + userOffHis.length + ' 건',
                                    onChange: (page, pageSize) => {
                                        dispatch(Usr0101mAction.setCurrent(page));
                                        dispatch(Usr0101mAction.setPageSize(pageSize));
                                    },
                                }}
                            />
                        </div>
                        : 
                        <div key={tableKey} style={{ height: contentHeight }}>
                            <Calendar
                                locale={koKR}
                                value={calValue}
                                headerRender={() => <></>}
                                className='custom-calendar'
                                disabledDate={(date) => {
                                    return !date.isSame(calValue, 'month');
                                }}
                                onSelect={(date:Dayjs) => {
                                    if (!date.isSame(calValue, 'month')) {
                                        return;
                                    }
                                    if (!date.isSame(calValue, 'day')) {
                                        setCalValue(date);
                                    }
                                }}
                                dateFullCellRender={(value) => {
                                    const isToday = value.isSame(dayjs(), 'day');
                                    const currents = calendarInfoList.filter(item => {
                                        return item.baseDt === value.format('YYYYMMDD');
                                    });

                                    const current = currents[0];

                                    const listData   = getListData(value);
                                    const day = value.day();
                                    const isHoliday = current?.holidayYn === 'Y';

                                    let tcnCtt = '';
                                    let color = '';
                                    if (day === 6) color = 'blue';
                                    if (day === 0) color = 'red';
                                    if (isHoliday) color = 'red';

                                    if (listData.length > 1) {
                                        tcnCtt = listData[0].content.split(' ')[0] + ' ' + listData[0].content.split(' ')[1] + ' 외 ' + (listData.length - 1) + '건';
                                    }
                                
                                    return (
                                      <div
                                        style={{
                                          height: contentHeight / 6.5, // 셀 높이
                                          display: 'flex',
                                          flexDirection: 'column',
                                          padding: 4,
                                          borderRadius: 6,
                                          background: isToday ? '#e6f4ff' : '#fff',
                                          border: isToday ? '2px solid #1677ff' : '1px solid #f0f0f0',
                                        }}
                                      >
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                width: '100%',
                                                whiteSpace: 'nowrap',
                                                paddingInline: 4,
                                                boxSizing: 'border-box',
                                                userSelect: 'none',
                                            }}
                                        >
                                            {/* 건수내용 */}
                                            <div
                                                style={{
                                                    flex: 1,
                                                    minWidth: 0,
                                                    height: 18,
                                                    lineHeight: '18px',
                                                    fontSize: 12,
                                                    color: '#fff',
                                                    backgroundColor: tcnCtt ? '#595959' : 'transparent',
                                                    borderRadius: 10,
                                                    padding: tcnCtt ? '0 6px' : 0,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    marginRight: 4,
                                                    textAlign: 'center',
                                                }}
                                            >
                                                {tcnCtt}
                                            </div>

                                            {/* 날짜 */}
                                            <div
                                                style={{
                                                    color,
                                                    fontWeight: 600,
                                                    marginBottom: 4,
                                                    flexShrink: 0,
                                                    marginRight: 5,
                                                }}
                                            >
                                                {value.date()}
                                            </div>
                                        </div>
                                
                                        {/* 내용 영역: 스크롤 처리 */}
                                        <div
                                          style={{
                                            flexGrow: 1,
                                            overflowY: 'auto',
                                            textAlign: 'left',
                                            marginLeft: 5,
                                          }}
                                        >
                                          {dateCellRender(value, listData)}
                                        </div>
                                      </div>
                                    );
                                }}
                            />
                        </div>
                        }
                    </Row>
                </Content>
            </Layout>
        </>
    );
};

export default Usr0101m;

